"use client";

import { sum } from "es-toolkit";
import { useRouter } from "next/navigation";
import type { ReactNode } from "react";
import { useEffect, useState } from "react";

import { Skeleton } from "@/components/ui/skeleton";
import { useFocusMarketData } from "@/contexts/focus-market-data";
import {
  useUserAccountSignalState,
  useUserAuthSignalState,
  useUserBalancesSignalState,
} from "@/contexts/user-context";
import {
  DESO_APP_NAME,
  DESO_TICKER_DISPLAY_NAME,
  FOCUS_TICKER_DISPLAY_NAME,
  FOCUS_TOKEN_DISPLAY_NAME,
  USDC_DISPLAY_NAME,
} from "@/utils/constants";
import {
  desoToUSD,
  focusToUsd,
  formatUSD,
  nanosToInteger,
} from "@/utils/currency";

import { WalletCurrencyRow } from "./wallet-currency-row";
import { WalletLockedTokensCell } from "./wallet-locked-tokens-cell";

interface WalletBalanceTableProps {
  desoBalance?: number;
  dusdBalance?: number;
  focusBalance?: number;
  focusLockedBalance?: number;
  isManualRefresh?: boolean;
  holderPublicKey: string;
  showCrossChainCurrencies?: boolean;
  wrappedAssetBalances?: {
    BTC: number;
    ETH: number;
    SOL: number;
  };
  iconSize?: number;
  showCoreCurrencies?: boolean;
}

const DEFAULT_ICON_SIZE = 30;

export function WalletBalanceTable({
  desoBalance = 0,
  dusdBalance = 0,
  focusBalance = 0,
  focusLockedBalance = 0,
  isManualRefresh,
  holderPublicKey,
  showCrossChainCurrencies,
  wrappedAssetBalances,
  iconSize = DEFAULT_ICON_SIZE,
  showCoreCurrencies = true,
}: WalletBalanceTableProps) {
  const router = useRouter();

  const { loading: loadingUser } = useUserAccountSignalState();
  const { currentUserPublicKey } = useUserAuthSignalState();
  const {
    realTimeDesoBalanceNanos,
    realTimeDusdBalanceNanos,
    realTimeFocusUnlockedBalanceNanos,
    realTimeFocusLockedBalanceNanos,
  } = useUserBalancesSignalState();
  const {
    appState,
    focusExchangeRate,
    loading: loadingFocusExchangeRate,
  } = useFocusMarketData();

  const isMyWallet = holderPublicKey === currentUserPublicKey;

  const [loading, setLoading] = useState(isMyWallet);

  useEffect(() => {
    setLoading(loadingUser);
  }, [loadingUser]);

  const renderBalanceValue = (
    content: (params: {
      desoExchangeRate: number;
      focusExchangeRate: number;
    }) => ReactNode,
  ) => {
    return loading ||
      isManualRefresh ||
      !appState ||
      loadingFocusExchangeRate ||
      !focusExchangeRate ? (
      <Skeleton className="h-5 w-[100px]" />
    ) : (
      content({
        desoExchangeRate: appState.USDCentsPerDeSoExchangeRate,
        focusExchangeRate,
      })
    );
  };

  const balances = isMyWallet
    ? {
        deso: nanosToInteger(realTimeDesoBalanceNanos),
        dusd: nanosToInteger(realTimeDusdBalanceNanos),
        focusUnlocked: nanosToInteger(realTimeFocusUnlockedBalanceNanos),
        focusLocked: nanosToInteger(realTimeFocusLockedBalanceNanos),
      }
    : {
        deso: desoBalance,
        dusd: dusdBalance,
        focusUnlocked: focusBalance,
        focusLocked: focusLockedBalance,
      };

  const getTotalBalance = (
    desoExchangeRate: number,
    focusExchangeRate: number,
  ) =>
    sum([
      desoToUSD(balances.deso, desoExchangeRate),
      balances.dusd,
      focusToUsd(balances.focusUnlocked, focusExchangeRate),
    ]);

  const crossChainCurrencies = [
    {
      name: "BTC",
      icon: "/assets/coin-btc.png",
      chainName: "Bitcoin",
      balance: wrappedAssetBalances?.BTC ?? 0,
      tooltip: `BTC on Openfund is actually BTC-DESO, which is wrapped BTC held in full self-custody via your ${DESO_APP_NAME} wallet.`,
    },
    {
      name: "ETH",
      icon: "/assets/coin-eth.png",
      chainName: "Ethereum",
      balance: wrappedAssetBalances?.ETH ?? 0,
      tooltip: `ETH on Openfund is actually ETH-DESO, which is wrapped ETH held in full self-custody via your ${DESO_APP_NAME} wallet.`,
    },
    {
      name: "SOL",
      icon: "/assets/coin-sol.png",
      chainName: "Solana",
      balance: wrappedAssetBalances?.SOL ?? 0,
      tooltip: `SOL on Openfund is actually SOL-DESO, which is wrapped SOL held in full self-custody via your ${DESO_APP_NAME} wallet.`,
    },
  ];

  return (
    <div className="mt-4 grid w-full items-start rounded-xl border">
      <div className="flex flex-col items-start text-left text-sm">
        {showCoreCurrencies && (
          <>
            <WalletCurrencyRow
              iconSrc="/assets/coin-deso.png"
              chainName={DESO_APP_NAME}
              currencyName={DESO_TICKER_DISPLAY_NAME}
              balance={balances.deso}
              usdValue={desoToUSD(
                balances.deso,
                appState?.USDCentsPerDeSoExchangeRate || 0,
              )}
              iconSize={iconSize}
              renderBalanceValue={renderBalanceValue}
            />

            <WalletCurrencyRow
              iconSrc="/assets/coin-usdc.png"
              chainName="USDC-DESO"
              currencyName={`$${USDC_DISPLAY_NAME}`}
              balance={balances.dusd}
              usdValue={balances.dusd}
              iconSize={iconSize}
              renderBalanceValue={renderBalanceValue}
            />

            <WalletCurrencyRow
              iconSrc="/assets/coin-focus.png"
              currencyName={`${FOCUS_TICKER_DISPLAY_NAME}`}
              chainName="Focus"
              balance={balances.focusUnlocked ?? 0}
              usdValue={focusToUsd(
                balances.focusUnlocked ?? 0,
                focusExchangeRate ?? 0,
              )}
              iconSize={iconSize}
              renderBalanceValue={renderBalanceValue}
            />

            <WalletCurrencyRow
              iconSrc="/assets/coin-focus.png"
              chainName="Locked Focus"
              currencyName={`${FOCUS_TICKER_DISPLAY_NAME}`}
              balance={balances.focusLocked ?? 0}
              usdValue={focusToUsd(
                balances.focusLocked ?? 0,
                focusExchangeRate ?? 0,
              )}
              iconSize={iconSize}
              renderBalanceValue={renderBalanceValue}
              isLocked={true}
              renderUnlockValue={() => (
                <WalletLockedTokensCell
                  refetchData={() => router.refresh()}
                  totalLockedUsdValue={balances.focusLocked}
                  coinPublicKey={process.env.NEXT_PUBLIC_FOCUS_APP_PUBLIC_KEY}
                  coinUsername={FOCUS_TOKEN_DISPLAY_NAME}
                  coinPrice={focusExchangeRate}
                  holderPublicKey={holderPublicKey}
                  showValue={false}
                  triggerBtnClassName="mt-2 h-3 mb-0 ml-auto"
                />
              )}
            />
          </>
        )}

        {showCrossChainCurrencies &&
          crossChainCurrencies.map((currency) => (
            <WalletCurrencyRow
              key={currency.name}
              iconSrc={currency.icon}
              currencyName={`$${currency.name}`}
              balance={currency.balance}
              usdValue={0}
              iconSize={iconSize}
              renderBalanceValue={() => null}
              externalTrade={true}
              chainName={currency.chainName}
            />
          ))}

        {showCoreCurrencies && (
          <div
            className="
              flex w-full flex-col items-center justify-between rounded-b-xl p-3
              sm:flex-row sm:gap-2
            "
          >
            <div className="flex items-center gap-2 text-muted">
              Total Spendable Balance
            </div>

            <span
              className="
                font-shadow-green font-mono text-lg text-emerald-400
                sm:text-sm
              "
            >
              {renderBalanceValue(({ desoExchangeRate, focusExchangeRate }) => (
                <>
                  ~
                  {formatUSD(
                    getTotalBalance(desoExchangeRate, focusExchangeRate),
                  )}{" "}
                  USD
                </>
              ))}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
